import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  ClipboardList,
  Wrench,
  Cpu,
  Search,
  Plus,
  Calendar,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  FileText,
  CheckCircle2,
  MapPin,
  Trash2,
  Edit,
  ArrowLeft,
  XCircle,
  Eye,
  EyeOff
} from "lucide-react";
import { OrderListItem, OrdersSummary, OrderItemType } from "./orders.types";
import { OrdersController } from "./orders.controller";
import { OrdersModel } from "./orders.model";
import { useApp } from "../../context/AppContext";
import { ProductsModel } from "../products/products.model";
import { ProductItem } from "../products/products.types";
import ProductionModal from "../../components/production/ProductionModal.view";
import { UsersController } from "../settings/users.controller";
import { StatusController } from "../settings/status.controller";
import { StatusItem } from "../settings/status.types";

/**
 * Helper to determine the next sequential sector and its design color.
 */
function getNextSector(currentSectorName: string): { name: string; colorHex: string } | null {
  const list = [
    { name: 'Corte', colorHex: '#0d6efd' },
    { name: 'Dobra', colorHex: '#fd7e14' },
    { name: 'Solda', colorHex: '#0d6efd' },
    { name: 'Acabamento', colorHex: '#198754' },
    { name: 'Acessórios', colorHex: '#0dcaf0' },
    { name: 'Embalagem', colorHex: '#6c757d' },
    { name: 'Logística', colorHex: '#6c757d' },
    { name: 'Instalação', colorHex: '#6c757d' }
  ];
  const curLower = (currentSectorName || "").toLowerCase();
  const currentIndex = list.findIndex(s => s.name.toLowerCase() === curLower || (curLower === 'coleta' && s.name.toLowerCase() === 'logística'));
  
  if (currentIndex >= 0 && currentIndex < list.length - 1) {
    return list[currentIndex + 1];
  }
  return null; // already at last sector
}

/**
 * Converts pt-BR date (DD/MM/YYYY or DD/MM/YY) to HTML date (YYYY-MM-DD).
 */
function toHtmlDate(ptBrDate: string): string {
  if (!ptBrDate) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(ptBrDate)) return ptBrDate;
  const parts = ptBrDate.split('/');
  if (parts.length === 3) {
    const day = parts[0].padStart(2, '0');
    const month = parts[1].padStart(2, '0');
    let year = parts[2];
    if (year.length === 2) year = '20' + year;
    return `${year}-${month}-${day}`;
  }
  return ptBrDate;
}

/**
 * Formats a date string (YYYY-MM-DD) to Brazilian format (DD/MM/YYYY).
 */
function formatBrazilianDate(dateStr: string): string {
  if (!dateStr) return '';
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
}

/**
 * Computes total elapsed production time for a given set of saved periods.
 */
function calculateTotalTimeForPeriods(periods: any[]): string {
  if (!periods || periods.length === 0) return "";
  let totalMinutes = 0;
  periods.forEach(p => {
    if (p.start && p.finish) {
      const [hStart, mStart] = p.start.split(':').map(Number);
      const [hFinish, mFinish] = p.finish.split(':').map(Number);
      if (!isNaN(hStart) && !isNaN(mStart) && !isNaN(hFinish) && !isNaN(mFinish)) {
        const startMin = hStart * 60 + mStart;
        const finishMin = hFinish * 60 + mFinish;
        if (finishMin >= startMin) {
          totalMinutes += (finishMin - startMin);
        }
      }
    }
  });

  if (totalMinutes === 0) return "";
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) {
    return `${hours}h${minutes > 0 ? ` ${minutes}min` : ''}`;
  }
  return `${minutes}min`;
}

/**
 * Helper to map sector/status/color to Bootstrap 5 background classes.
 * Supports our technical scope of Corte, Dobra, Solda, Acabamento, Acessórios, Embalagem, Emergência, Aguardando.
 */
function getSectorBgClass(status: string, colorHex?: string): string {
  const norm = (status || "").toLowerCase();

  // Emergência / Alerta
  if (norm.includes("emergencia") || norm.includes("emergência") || norm.includes("alerta") || colorHex === "#dc3545") {
    return "danger";
  }

  // Precedence of colorHex to preserve high fidelity colors from screen mockups
  if (colorHex) {
    if (colorHex === "#198754" || colorHex === "#success") return "success";
    if (colorHex === "#fd7e14" || colorHex === "#orange") return "orange";
    if (colorHex === "#0d6efd" || colorHex === "#blue" || colorHex === "#primary") return "primary";
    if (colorHex === "#ffc107" || colorHex === "#warning" || colorHex === "#gold" || colorHex === "#yellow") return "warning";
    if (colorHex === "#6c757d" || colorHex === "#secondary") return "secondary";
  }

  // Regular text/sector match
  if (norm.includes("corte") || norm.includes("solda") || norm.includes("produção") || norm.includes("producao")) {
    return "primary";
  }
  if (norm.includes("dobra") || norm.includes("setor/colaborador") || norm.includes("colaborador")) {
    return "orange";
  }
  if (norm.includes("aguardando")) {
    return "success";
  }
  if (norm.includes("acabamento")) {
    return "success";
  }
  if (norm.includes("acessórios") || norm.includes("acessorios")) {
    return "info";
  }
  if (norm.includes("embalagem") || norm.includes("finalizado") || norm.includes("concluido")) {
    return "secondary";
  }
  if (norm.includes("atrasado") || norm.includes("atrazado")) {
    return "danger";
  }

  return "success"; // Default status inicial is green
}

/**
 * Parses a date string supporting both YYYY-MM-DD and DD/MM/YYYY formats safely.
 */
function parseOrderDate(dateStr: string): Date | null {
  if (!dateStr) return null;
  const cleanStr = dateStr.trim();
  if (cleanStr.includes('-')) {
    const parts = cleanStr.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        return new Date(year, month, day);
      }
    }
  }
  if (cleanStr.includes('/')) {
    const parts = cleanStr.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      let year = parseInt(parts[2], 10);
      if (year < 100) {
        year += 2000;
      }
      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        return new Date(year, month, day);
      }
    }
  }
  return null;
}

/**
 * Helper to get exact HEX color code for absolute specificity in Bootstrap cells.
 */
function getSectorBgColorHex(status: string, colorHex?: string): string {
  const norm = (status || "").toLowerCase();
  if (colorHex && colorHex.startsWith('#')) {
    return colorHex;
  }
  if (norm.includes("atrasado") || norm.includes("atrazado")) {
    return "#dc3545"; // danger red
  }
  if (norm.includes("emergencia") || norm.includes("emergência") || norm.includes("alerta") || colorHex === "#dc3545") {
    return "#dc3545"; // danger red
  }
  if (norm.includes("corte") || norm.includes("solda") || norm.includes("produção") || norm.includes("producao") || colorHex === "#0d6efd") {
    return "#0d6efd"; // primary blue
  }
  if (norm.includes("dobra") || norm.includes("setor/colaborador") || norm.includes("colaborador") || colorHex === "#fd7e14") {
    return "#fd7e14"; // orange
  }
  if (norm.includes("aguardando") || norm.includes("acabamento") || colorHex === "#198754") {
    return "#009e53"; // solid beautiful green
  }
  if (norm.includes("acessórios") || norm.includes("acessorios") || colorHex === "#0dcaf0") {
    return "#0dcaf0"; // info cyan
  }
  if (norm.includes("embalagem") || norm.includes("finalizado") || norm.includes("concluido") || colorHex === "#6c757d") {
    return "#6c757d"; // secondary gray
  }
  return "#009e53"; // default green
}

export default function OrdersView() {
  const location = useLocation();
  const { activeOperator } = useApp();

  const isAdm = activeOperator.role === 'ADM';
  const canAddPedido = isAdm || activeOperator.permissions?.add?.pedido === true;
  const canEditPedido = isAdm || activeOperator.permissions?.edit?.pedido === true;
  const canDeletePedido = isAdm || activeOperator.permissions?.delete?.pedido === true;
  
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<OrderListItem[]>([]);
  const [summary, setSummary] = useState<OrdersSummary>({
    totalOrders: 0,
    totalProducts: 0,
    inProduction: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Accordion UI state tracking (defaults to expanding the first order 621 like in Page 4)
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(1);
  const [isInsertingOrder, setIsInsertingOrder] = useState<boolean>(() => {
    return !!(location.state && (location.state as any).openInsert);
  });
  const [editingOrder, setEditingOrder] = useState<OrderListItem | null>(null);

  useEffect(() => {
    if (location.state && (location.state as any).openInsert) {
      setIsInsertingOrder(true);
    }
  }, [location]);

  // Custom statuses preloaded from the status registry
  const [statusList, setStatusList] = useState<StatusItem[]>(() => StatusController.list());

  useEffect(() => {
    StatusController.fetchList().then(list => setStatusList(list));
  }, [isInsertingOrder, editingOrder]);
  const [registerSearch, setRegisterSearch] = useState<string>("");
  const [selectedPeriod, setSelectedPeriod] = useState<number>(7);
  const [searchText, setSearchText] = useState<string>( "");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [isPeriodDropdownOpen, setIsPeriodDropdownOpen] = useState<boolean>(false);
  const [hideOverdue, setHideOverdue] = useState<boolean>(false);

  // Close orders page dropdown on click outside
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('#orders-period-dropdown') && !target.closest('.dropdown-menu')) {
        setIsPeriodDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  // States for Production modal
  const [productionModalOpen, setProductionModalOpen] = useState<boolean>(false);
  const [selectedOrderNumber, setSelectedOrderNumber] = useState<string>("");
  const [selectedItemId, setSelectedItemId] = useState<string>("");
  const [selectedItemDescription, setSelectedItemDescription] = useState<string>("");
  const [selectedClientName, setSelectedClientName] = useState<string>("");
  const [selectedCity, setSelectedCity] = useState<string>("");
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState<string>("");
  const [selectedWeekday, setSelectedWeekday] = useState<string>("");

  // Simulated Page 5 Insert Order list items state
  const [insertItems, setInsertItems] = useState<string[]>(['Coifa Alfa']);
  const [availableProducts, setAvailableProducts] = useState<ProductItem[]>(() => ProductsModel.getProducts());

  const handleOpenProductionModal = (
    orderNum: string | number, 
    itemId: string, 
    description: string,
    client?: string,
    city?: string,
    date?: string,
    weekday?: string
  ) => {
    setSelectedOrderNumber(String(orderNum));
    setSelectedItemId(itemId);
    setSelectedItemDescription(description);
    setSelectedClientName(client || "Restaurante A");
    setSelectedCity(city || "Paraguaçu Paulista");
    setSelectedDeliveryDate(date || "10/08/26");
    setSelectedWeekday(weekday || "Segunda-Feira");
    setProductionModalOpen(true);
  };

  const searchFormRef = useRef<HTMLFormElement>(null);

  const controller = new OrdersController(
    setOrders,
    setFilteredOrders,
    setSummary,
    setLoading,
    setError,
  );

  useEffect(() => {
    controller.load();
    async function loadProducts() {
      try {
        const list = await ProductsModel.fetchProducts();
        setAvailableProducts(list);
        setInsertItems(prev => {
          if (prev.length === 1 && prev[0] === 'Coifa Alfa' && list.length > 0) {
            const hasAlfa = list.some(p => p.name === 'Coifa Alfa');
            if (!hasAlfa) {
              return [list[0].name];
            }
          }
          return prev;
        });
      } catch (err) {
        console.error("Erro ao carregar produtos para inserção de ordens:", err);
      }
    }
    loadProducts();
  }, []);

  const handleSearchInput = (e: React.FormEvent<HTMLInputElement>) => {
    setSearchText(e.currentTarget.value);
  };

  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Mapear se o pedido está atrasado e definir seu status para "Atrasado" se for o caso
    let result = orders.map(order => {
      const orderDate = parseOrderDate(order.departure);
      const isFinalized = (order.statusName || "").toLowerCase() === 'finalizado';
      const isOverdue = orderDate ? (orderDate < today && !isFinalized) : false;
      return {
        ...order,
        isOverdue,
        statusName: isOverdue ? "Atrasado" : order.statusName
      };
    });

    // 1. Aplicar filtro de período de data para o futuro
    result = result.filter(order => {
      // Se for atrasado e hideOverdue estiver ativo, filtramos para fora
      if (order.isOverdue) {
        return !hideOverdue;
      }

      const orderDate = parseOrderDate(order.departure);
      if (orderDate) {
        // A lista exibe sempre a partir da data atual (hoje) para o futuro
        if (orderDate < today) {
          return false; // se for passado e não for atrasado (ex: finalizado), esconde
        }

        if (selectedPeriod > 0) {
          const limitDate = new Date(today);
          limitDate.setDate(today.getDate() + selectedPeriod);
          return orderDate <= limitDate;
        }
      }
      return true;
    });

    // 1.5. Aplicar filtro de status
    if (statusFilter) {
      result = result.filter(order => {
        const orderStatus = (order.statusName || "").toLowerCase();
        const customStatus = (order.customStatus || "").toLowerCase();
        const filterLower = statusFilter.toLowerCase();
        return orderStatus === filterLower || customStatus === filterLower || (order.isOverdue && filterLower === 'atrasado');
      });
    }

    // 2. Aplicar filtro de busca por texto
    const term = searchText.toLowerCase().trim();
    if (term) {
      const includesFinalizado = term.includes('finalizado') || term.includes('concluido') || term.includes('entregue') || term.includes('atrasado') || term.includes('atrazado');
      result = result.filter(order => {
        if (order.statusName.toLowerCase() === 'finalizado' && !includesFinalizado) {
          return false;
        }
        return (
          order.number.toString().includes(term) ||
          order.customerName.toLowerCase().includes(term) ||
          order.city.toLowerCase().includes(term) ||
          order.statusName.toLowerCase().includes(term) ||
          (order.customStatus || '').toLowerCase().includes(term)
        );
      });
    } else if (statusFilter.toLowerCase() === 'finalizado') {
      // Se o filtro de status selecionado for explicitamente Finalizado, mantemos somente finalizados
      result = result.filter(order => order.statusName.toLowerCase() === 'finalizado');
    } else {
      // Por padrão (sem busca ou filtro de finalizado), esconde finalizados para focar no fluxo ativo do chão de fábrica
      result = result.filter(order => order.statusName.toLowerCase() !== 'finalizado');
    }

    // Ordenar por proximidade de data de previsão de saída (prazos mais curtos/atrasados no topo)
    result.sort((a, b) => {
      const dateA = parseOrderDate(a.departure);
      const dateB = parseOrderDate(b.departure);
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }
      return String(a.number).localeCompare(String(b.number), undefined, { numeric: true, sensitivity: 'base' });
    });

    setFilteredOrders(result);

    // 3. Recalcular e atualizar o summary reativamente com base nos pedidos filtrados
    const totalOrders = result.length;
    let totalProducts = 0;
    let inProduction = 0;

    result.forEach(o => {
      if (o.items) {
        totalProducts += o.items.length;
        o.items.forEach(it => {
          const s = (it.status || '').toLowerCase();
          if (s.includes('produção') || s.includes('producao')) {
            inProduction++;
          }
        });
      }
    });

    setSummary({
      totalOrders,
      totalProducts,
      inProduction
    });

  }, [orders, selectedPeriod, searchText, hideOverdue, statusFilter]);

  const handleCreateOrderSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    // Convert current insertItems array to comma-separated string for controller
    formData.append('itemNames', insertItems.join(','));
    
    if (editingOrder) {
      const success = await controller.updateOrder(editingOrder.id, formData, orders);
      if (success) {
        setIsInsertingOrder(false);
        setEditingOrder(null);
        setInsertItems(['Coifa Alfa']);
      }
    } else {
      const success = await controller.addOrder(formData, orders);
      if (success) {
        setIsInsertingOrder(false);
        setInsertItems(['Coifa Alfa']);
      }
    }
  };

  const handleAddProductField = () => {
    const defaultProduct = availableProducts[0]?.name || 'Coifa Alfa';
    setInsertItems([...insertItems, defaultProduct]);
  };

  const handleUpdateProductField = (idx: number, val: string) => {
    const updated = [...insertItems];
    updated[idx] = val;
    setInsertItems(updated);
  };

  const handleRemoveProductField = (idx: number) => {
    setInsertItems(insertItems.filter((_, i) => i !== idx));
  };

  // -------------------------------------------------------------
  // VIEW RENDER A: INSERT ORDER SCREEN (PAGE 5 VISUAL REPLICA)
  // -------------------------------------------------------------
  if (isInsertingOrder) {
    const searchLower = registerSearch.toLowerCase().trim();
    const listForRegistration = orders.filter(o => {
      if (!searchLower) return true;
      return (
        o.number.toString().includes(searchLower) ||
        o.customerName.toLowerCase().includes(searchLower) ||
        o.city.toLowerCase().includes(searchLower) ||
        o.statusName.toLowerCase().includes(searchLower)
      );
    });

    return (
      <div className="fade-in px-1" id="insert-order-form-page">
        {/* Breadcrumb Path Headers */}
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center border-bottom pb-3 mb-4 gap-2">
          <div>
            <div className="text-secondary text-xs font-mono font-bold uppercase tracking-wider mb-1">
              Pedido &gt; Lista de Pedidos &gt; {editingOrder ? 'Editar pedido' : 'Inserir pedido'}
            </div>
            <h1 className="h4 fw-extrabold text-dark font-display mb-0">{editingOrder ? `Editar Pedido #${editingOrder.number}` : 'Novo Pedido de Coifas'}</h1>
          </div>
          
          <div className="d-flex align-items-center gap-2">
            <button 
              type="button" 
              onClick={() => {
                setIsInsertingOrder(false);
                setEditingOrder(null);
                setInsertItems(['Coifa Alfa']);
              }}
              className="btn btn-outline-secondary px-4 py-1.5 font-display text-sm"
              style={{ borderRadius: '6px' }}
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              form="page5-insert-form"
              className="btn btn-primary px-4 py-1.5 font-display text-sm"
              style={{ backgroundColor: '#2f66aa', borderColor: '#2f66aa', borderRadius: '6px' }}
            >
              Salvar
            </button>
          </div>
        </div>

        {/* Page 5 Form Card with key reset to support uncontrolled preloads on toggle */}
        <div className="card shadow-sm border border-secondary border-opacity-10 bg-white p-4 rounded mb-5" id="form-card-wrapper">
          <form id="page5-insert-form" key={editingOrder ? editingOrder.id : 'new'} onSubmit={handleCreateOrderSubmit}>
            <div className="row g-4">
              
              {/* Prioridade & Emergência Checkboxes (unchecked by default for normal registration flow) */}
              <div className="col-12 d-flex flex-wrap gap-4 align-items-center border-bottom pb-3 mb-2">
                <div className="form-check d-flex align-items-center gap-2">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    name="priority" 
                    value="true" 
                    id="chk-prioridade" 
                    defaultChecked={editingOrder ? editingOrder.isPriority : false} 
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label className="form-check-label text-sm fw-bold text-dark font-sans" htmlFor="chk-prioridade" style={{ userSelect: 'none', cursor: 'pointer' }}>
                    Prioridade
                  </label>
                </div>

                <div className="form-check d-flex align-items-center gap-2">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    name="emergency" 
                    value="true" 
                    id="chk-emergencia" 
                    defaultChecked={editingOrder ? editingOrder.isEmergence : false}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label className="form-check-label text-sm fw-bold text-danger font-sans" htmlFor="chk-emergencia" style={{ userSelect: 'none', cursor: 'pointer' }}>
                    Emergência
                  </label>
                </div>
              </div>

              {/* Form Input fields */}
              <div className="col-12 col-md-3">
                <label className="form-label text-xxs text-muted text-uppercase fw-extrabold tracking-wider font-mono">Nº do pedido</label>
                <input 
                  type="text" 
                  name="orderNumber" 
                  required 
                  className="form-control" 
                  placeholder="Ex: 622" 
                  defaultValue={editingOrder ? editingOrder.number : ''}
                  style={{ borderRadius: '6px' }}
                />
              </div>

              <div className="col-12 col-md-9">
                <label className="form-label text-xxs text-muted text-uppercase fw-extrabold tracking-wider font-mono">Cliente</label>
                <input 
                  type="text" 
                  name="customerName" 
                  required 
                  className="form-control" 
                  placeholder="Nome do cliente corporativo ou residencial" 
                  defaultValue={editingOrder ? editingOrder.customerName : ''}
                  style={{ borderRadius: '6px' }}
                />
              </div>

              <div className="col-12 col-md-3">
                <label className="form-label text-xxs text-muted text-uppercase fw-extrabold tracking-wider font-mono">Previsão de Saída</label>
                <div className="input-group">
                  <input 
                    type="date" 
                    name="departure" 
                    className="form-control text-dark font-mono" 
                    defaultValue={toHtmlDate(editingOrder ? editingOrder.departure : '10/08/2026')}
                    style={{ borderRight: 'none', borderRadius: '6px 0 0 6px' }}
                  />
                  <span className="input-group-text bg-white border-start-0 text-muted">
                    <Calendar size={16} />
                  </span>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label text-xxs text-muted text-uppercase fw-extrabold tracking-wider font-mono">Cidade</label>
                <input 
                  type="text" 
                  name="city" 
                  required 
                  className="form-control" 
                  placeholder="Ex: Paraguaçú Paulista" 
                  defaultValue={editingOrder ? editingOrder.city : ''}
                  style={{ borderRadius: '6px' }}
                />
              </div>

              <div className="col-12 col-md-3">
                <label className="form-label text-xxs text-muted text-uppercase fw-extrabold tracking-wider font-mono">Status Personalizado</label>
                <select
                  name="customStatus"
                  className="form-select font-sans"
                  style={{ borderRadius: '6px', height: '38px', cursor: 'pointer' }}
                  defaultValue={editingOrder ? (editingOrder.customStatus || '') : ''}
                  disabled={!canEditPedido}
                >
                  <option value="">(Nenhum - Vazio)</option>
                  {statusList.filter(st => !st.isSystem).map(st => (
                    <option key={st.id + '-' + st.name} value={st.name}>
                      {st.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Added list items - Page 5 interactive subtable */}
            <div className="mt-5 border-top pt-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="fw-bold text-dark font-display mb-0">Composição do Pedido (Peças / Equipamentos)</h5>
                <button 
                  type="button" 
                  onClick={handleAddProductField}
                  className="btn btn-primary btn-sm d-flex align-items-center gap-1 font-display"
                  style={{ backgroundColor: '#2f66aa', borderColor: '#2f66aa' }}
                >
                  <Plus size={15} /> Adicionar produto
                </button>
              </div>

              {/* DESKTOP VIEW OF COMPOSITION SUBTABLE */}
              <div className="table-responsive rounded border bg-light-subtle p-3 d-none d-md-block">
                <table className="table table-borderless align-middle mb-0">
                  <thead className="border-bottom text-muted">
                    <tr>
                      <th style={{ width: '80px' }} className="font-mono text-xs uppercase text-secondary">Ítem</th>
                      <th className="font-mono text-xs uppercase text-secondary">Nome do Produto</th>
                      <th style={{ width: '120px' }} className="text-end font-mono text-xs uppercase text-secondary">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {insertItems.map((val, idx) => (
                      <tr key={idx} className="border-bottom border-light">
                        <td className="fw-bold font-mono text-dark">{idx + 1}</td>
                        <td>
                          {/* Rich select list or editable inputs */}
                          <select 
                            className="form-select font-sans py-1.5"
                            value={val}
                            onChange={(e) => handleUpdateProductField(idx, e.target.value)}
                            style={{ borderRadius: '6px', cursor: 'pointer' }}
                          >
                            {availableProducts.map(p => (
                              <option key={p.id} value={p.name}>{p.name}</option>
                            ))}
                            {availableProducts.length === 0 && (
                              <option value="">Nenhum produto cadastrado</option>
                            )}
                          </select>
                        </td>
                        <td className="text-end">
                          <div className="d-flex align-items-center justify-content-end gap-2">
                            <button 
                              type="button" 
                              title="Editar" 
                              className="btn btn-outline-secondary btn-sm p-1 border-0"
                              onClick={() => alert(`Editar detalhes do item de catálogo`)}
                            >
                              <Edit size={16} className="text-muted" />
                            </button>
                            <button 
                              type="button" 
                              title="Remover" 
                              className="btn btn-link text-danger p-1 border-0"
                              disabled={insertItems.length === 1}
                              onClick={() => handleRemoveProductField(idx)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* MOBILE/TABLET STACKED VIEW OF COMPOSITION SUBTABLE */}
              <div className="d-block d-md-none bg-light-subtle p-2 rounded border" id="insert-items-mobile-stack">
                {insertItems.map((val, idx) => (
                  <div key={idx} className="bg-white border rounded p-2.5 mb-2.5">
                    <div className="d-flex align-items-center justify-content-between mb-1.5">
                      <span className="badge bg-dark fw-bold font-mono text-xxs">EQUIPAMENTO #{idx + 1}</span>
                      <button 
                        type="button" 
                        title="Remover" 
                        className="btn btn-link link-danger p-1 border-0 text-danger"
                        disabled={insertItems.length === 1}
                        onClick={() => handleRemoveProductField(idx)}
                        style={{ height: '32px', width: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                    {/* Select box */}
                    <select 
                      className="form-select font-sans py-2"
                      value={val}
                      onChange={(e) => handleUpdateProductField(idx, e.target.value)}
                      style={{ borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}
                    >
                      {availableProducts.map(p => (
                        <option key={p.id} value={p.name}>{p.name}</option>
                      ))}
                      {availableProducts.length === 0 && (
                        <option value="">Nenhum produto cadastrado</option>
                      )}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </form>
        </div>

        {/* Visual Partition Header for Registered Orders List within Cadastro Screen */}
        <div className="border-top pt-4 mt-5 mb-4" id="registration-search-partition">
          <div className="mb-2">
            <h4 className="h5 fw-extrabold text-dark font-display mb-1">🔍 Pesquisa e Gerenciamento de Pedidos</h4>
            <p className="text-secondary text-xs font-sans mb-3">Busque, edite ou remova qualquer pedido cadastrado no sistema</p>
          </div>
          
          {/* Filter Input Box */}
          <div className="row justify-content-between align-items-center g-3 mb-4">
            <div className="col-12 col-md-5">
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0 text-muted">
                  <Search size={16} />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Pesquise por Nº, Cliente, Cidade ou Status..."
                  value={registerSearch}
                  onChange={(e) => setRegisterSearch(e.target.value)}
                  style={{ borderRadius: '0 6px 6px 0' }}
                />
                {registerSearch && (
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary border-0" 
                    onClick={() => setRegisterSearch('')}
                  >
                    <XCircle size={15} />
                  </button>
                )}
              </div>
            </div>
            <div className="col-auto text-muted font-mono text-xs">
              Exibindo <strong>{listForRegistration.length}</strong> de <strong>{orders.length}</strong> pedidos
            </div>
          </div>

          {/* DESKTOP TABLE VIEW */}
          <div className="card shadow-sm border border-secondary border-opacity-10 bg-white d-none d-lg-block">
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead className="bg-light text-muted font-mono text-xxs uppercase tracking-wider">
                  <tr>
                    <th scope="col" className="px-3 py-3 text-center" style={{ width: '80px' }}>Pedido</th>
                    <th scope="col" className="px-3 py-3">Cliente</th>
                    <th scope="col" className="px-3 py-3">Cidade</th>
                    <th scope="col" className="px-3 py-3">Composição / Peças</th>
                    <th scope="col" className="px-3 py-3 text-center" style={{ width: '150px' }}>Prioridade</th>
                    <th scope="col" className="px-3 py-3 text-center" style={{ width: '150px' }}>Status</th>
                    <th scope="col" className="px-3 py-3 text-end" style={{ width: '200px' }}>Ações</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {listForRegistration.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-5 text-muted">
                        <div className="py-2 text-md">Nenhum pedido encontrado.</div>
                      </td>
                    </tr>
                  ) : (
                    listForRegistration.map((ord) => (
                      <tr key={ord.id} className="border-bottom border-light">
                        <td className="px-3 py-3 text-center font-mono fw-bold text-dark bg-light-subtle">
                          #{ord.number}
                        </td>
                        <td className="px-3 py-3 fw-semibold text-dark">
                          {ord.customerName}
                        </td>
                        <td className="px-3 py-3 text-secondary">
                          <MapPin size={13} className="text-muted me-1" />
                          {ord.city}
                        </td>
                        <td className="px-3 py-3">
                          <div className="d-flex flex-wrap gap-1">
                            {ord.items.map((item, idx) => (
                              <span 
                                key={idx} 
                                className="badge bg-secondary bg-opacity-10 text-dark font-sans text-xxs fw-normal border"
                                style={{ padding: '3px 6px', borderRadius: '4px' }}
                              >
                                {item.productName}
                              </span>
                            ))}
                            {ord.items.length === 0 && <span className="text-muted text-xs">Sem itens cadastrados</span>}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <div className="d-flex flex-column align-items-center gap-1">
                            {ord.isEmergence && (
                              <span className="badge bg-danger text-white text-xxs px-2 py-1 uppercase fw-extrabold" style={{ letterSpacing: '0.5px' }}>
                                Emergência 🚨
                              </span>
                            )}
                            {ord.isPriority && (
                              <span className="badge bg-warning text-dark text-xxs px-2 py-1 uppercase fw-extrabold" style={{ letterSpacing: '0.5px' }}>
                                Prioritário ⚡
                              </span>
                            )}
                            {!ord.isEmergence && !ord.isPriority && (
                              <span className="text-muted text-xs">Normal</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <div className="d-flex flex-column align-items-center gap-1.5 justify-content-center">
                            <span 
                              className="badge text-white font-mono text-xxs uppercase px-2.5 py-1.5"
                              style={{ 
                                backgroundColor: getSectorBgColorHex(ord.statusName),
                                borderRadius: '4px',
                                letterSpacing: '0.5px'
                              }}
                            >
                              {ord.statusName}
                            </span>
                            {ord.customStatus && (
                              <span 
                                className="badge bg-light text-dark font-sans text-xxs border border-secondary border-opacity-30 px-2 py-0.5"
                                style={{ borderRadius: '4px' }}
                                title="Status Personalizado do Pedido"
                              >
                                {ord.customStatus}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-end">
                          <div className="d-inline-flex gap-2">
                            {canEditPedido && (
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingOrder(ord);
                                  setInsertItems(ord.items.map(it => it.productName).length > 0 ? ord.items.map(it => it.productName) : ['Coifa Alfa']);
                                  setIsInsertingOrder(true);
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1 border-primary-subtle"
                                style={{ borderRadius: '5px', padding: '4px 10px', fontSize: '0.75rem' }}
                              >
                                <Edit size={12} /> Editar
                              </button>
                            )}
                            {canDeletePedido && (
                              <button
                                type="button"
                                onClick={() => {
                                  if (confirm(`Deseja realmente apagar o pedido #${ord.number}? O cadastro e os processos serão removidos permanentemente.`)) {
                                    controller.deleteOrder(ord.id, orders);
                                    if (editingOrder?.id === ord.id) {
                                      setEditingOrder(null);
                                      setInsertItems(['Coifa Alfa']);
                                    }
                                  }
                                }}
                                className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1 border-danger-subtle"
                                style={{ borderRadius: '5px', padding: '4px 10px', fontSize: '0.75rem' }}
                              >
                                <Trash2 size={12} /> Excluir
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* MOBILE RESPONSIVE CARDS FEED VIEW */}
          <div className="d-block d-lg-none" id="orders-registration-mobile-feed">
            {listForRegistration.length === 0 ? (
              <div className="card shadow-xs border rounded-2 bg-white p-4 text-center text-muted text-sm">
                Nenhum pedido encontrado correspondente à pesquisa.
              </div>
            ) : (
              listForRegistration.map((ord) => (
                <div 
                  key={ord.id} 
                  className="card shadow-sm border mb-3 bg-white p-3 rounded-2"
                  style={{ borderColor: '#eaedf0' }}
                >
                  {/* Top line: Order Number + Priority Badges */}
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="font-mono text-xs text-dark fw-extrabold px-2 py-1 bg-light-subtle rounded border">
                      Pedido #{ord.number}
                    </span>
                    <div className="d-flex gap-1">
                      {ord.isEmergence && (
                        <span className="badge bg-danger text-white text-xxxs px-1.5 py-0.5 fw-bold uppercase">
                          🚨 Emerg.
                        </span>
                      )}
                      {ord.isPriority && (
                        <span className="badge bg-warning text-dark text-xxxs px-1.5 py-0.5 fw-bold uppercase">
                          ⚡ Prior.
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Body details */}
                  <div className="mb-2">
                    <h5 className="font-sans text-sm fw-bold text-dark mb-1">{ord.customerName}</h5>
                    <div className="text-secondary text-xs font-sans mb-2 d-flex align-items-center">
                      <MapPin size={12} className="text-muted p-0 me-1" />
                      {ord.city}
                    </div>

                    {/* Pieces Composition list */}
                    <div className="border-top pt-2 mt-2">
                      <div className="text-xxs text-muted text-uppercase font-mono fw-semibold mb-1">Equipamentos:</div>
                      <div className="d-flex flex-wrap gap-1">
                        {ord.items.map((item, idx) => (
                          <span 
                            key={idx} 
                            className="badge bg-light text-dark font-sans text-xxs fw-normal border"
                            style={{ padding: '2px 5px', borderRadius: '3px' }}
                          >
                            {item.productName}
                          </span>
                        ))}
                        {ord.items.length === 0 && <span className="text-muted text-xs">Sem itens</span>}
                      </div>
                    </div>
                  </div>

                  {/* Status sector and custom status badge */}
                  <div className="bg-light rounded p-2 mb-2.5 d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <span className="text-xxs font-mono text-muted text-uppercase fw-bold">Setor Atual:</span>
                    <div className="d-flex align-items-center gap-1.5">
                      <span 
                        className="badge text-white font-mono text-xxxs uppercase px-2 py-1 fw-bold"
                        style={{ 
                          backgroundColor: getSectorBgColorHex(ord.statusName),
                          borderRadius: '4px'
                        }}
                      >
                        {ord.statusName}
                      </span>
                      {ord.customStatus && (
                        <span className="badge bg-white text-dark border font-sans text-xxs py-1 px-2 rounded">
                          {ord.customStatus}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions utility panel */}
                  <div className="d-flex align-items-center justify-content-end gap-2 border-top pt-2">
                    {canEditPedido && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditingOrder(ord);
                          setInsertItems(ord.items.map(it => it.productName).length > 0 ? ord.items.map(it => it.productName) : ['Coifa Alfa']);
                          setIsInsertingOrder(true);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1 py-1.5 px-3"
                        style={{ fontSize: '0.8rem', borderRadius: '5px' }}
                      >
                        <Edit size={13} /> Editar
                      </button>
                    )}
                    {canDeletePedido && (
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(`Deseja realmente apagar o pedido #${ord.number}? O cadastro e os processos serão removidos permanentemente.`)) {
                            controller.deleteOrder(ord.id, orders);
                            if (editingOrder?.id === ord.id) {
                              setEditingOrder(null);
                              setInsertItems(['Coifa Alfa']);
                            }
                          }
                        }}
                        className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1 py-1.5 px-3"
                        style={{ fontSize: '0.8rem', borderRadius: '5px' }}
                      >
                        <Trash2 size={13} /> Excluir
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------
  // VIEW RENDER B: MAIN ORDERS LIST SCREEN (PAGE 4 VISUAL REPLICA)
  // -------------------------------------------------------------
  return (
    <div className="fade-in px-1" id="orders-main-viewport">
      
      {/* Title & Actions Row */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center border-bottom pb-3 mb-4 gap-2">
        <div>
          <h1 className="h3 fw-bold text-dark font-display mb-1 text-lowercase" style={{ letterSpacing: '-0.5px' }}>
            Lista de pedidos
          </h1>
          <p className="text-xs font-mono text-muted mb-0">Visão Geral e Controle Sequencial do Chão de Fábrica</p>
        </div>
        
        <div className="d-flex align-items-center gap-2">
          {(canAddPedido || canEditPedido) && (
            <button 
              type="button" 
              onClick={() => setIsInsertingOrder(true)}
              className="btn btn-primary d-flex align-items-center gap-1.5 px-3 py-2 font-display text-sm text-white"
              style={{ backgroundColor: '#2f66aa', borderColor: '#2f66aa', borderRadius: '6px' }}
            >
              <Plus size={16} /> Adicionar/Editar
            </button>
          )}

          <button
            type="button"
            onClick={() => setHideOverdue(!hideOverdue)}
            className={`btn d-flex align-items-center gap-1.5 px-3 py-2 text-sm font-sans fw-semibold ${hideOverdue ? 'btn-outline-danger' : 'btn-danger text-white'}`}
            style={{ borderRadius: '6px', fontSize: '0.8rem', minHeight: '38px', height: '38px', border: hideOverdue ? '1px solid rgba(220, 53, 69, 0.5)' : 'none' }}
          >
            {hideOverdue ? <Eye size={15} /> : <EyeOff size={15} />}
            {hideOverdue ? "Mostrar Atrasados" : "Ocultar Atrasados"}
          </button>

          <div className="dropdown">
            <button 
              type="button" 
              className={`btn bg-white border d-flex align-items-center gap-2 text-muted py-1.5 px-3 rounded dropdown-toggle border-light-subtle ${isPeriodDropdownOpen ? 'show' : ''}`}
              onClick={() => setIsPeriodDropdownOpen(!isPeriodDropdownOpen)}
              aria-expanded={isPeriodDropdownOpen}
              id="orders-period-dropdown"
              style={{ height: '38px' }}
            >
              <Calendar size={15} className="text-secondary" />
              <div className="text-start" style={{ fontSize: '0.8rem', lineHeight: '1.1' }}>
                <div className="font-mono text-muted text-xxs tracking-wider uppercase">
                  {selectedPeriod === 0 ? "Geral" : `Próximos ${selectedPeriod} dias`}
                </div>
                <div className="font-sans fw-bold text-dark">
                  {(() => {
                    if (selectedPeriod === 0) return "Todo o período";
                    const start = new Date();
                    const end = new Date();
                    end.setDate(start.getDate() + selectedPeriod);
                    const format = (d: Date) => {
                      const day = String(d.getDate()).padStart(2, '0');
                      const month = String(d.getMonth() + 1).padStart(2, '0');
                      return `${day}/${month}`;
                    };
                    return `${format(start)} - ${format(end)}`;
                  })()}
                </div>
              </div>
            </button>
            <ul 
              className={`dropdown-menu dropdown-menu-end shadow-sm ${isPeriodDropdownOpen ? 'show' : ''}`} 
              aria-labelledby="orders-period-dropdown"
              style={{ 
                display: isPeriodDropdownOpen ? 'block' : 'none', 
                position: 'absolute', 
                right: 0, 
                top: '100%',
                zIndex: 1050
              }}
            >
              <li>
                <button 
                  className={`dropdown-item font-sans text-xs ${selectedPeriod === 7 ? 'active fw-bold' : ''}`}
                  onClick={() => { setSelectedPeriod(7); setIsPeriodDropdownOpen(false); }}
                  type="button"
                >
                  Próximos 7 dias
                </button>
              </li>
              <li>
                <button 
                  className={`dropdown-item font-sans text-xs ${selectedPeriod === 15 ? 'active fw-bold' : ''}`}
                  onClick={() => { setSelectedPeriod(15); setIsPeriodDropdownOpen(false); }}
                  type="button"
                >
                  Próximos 15 dias (Quinzena)
                </button>
              </li>
              <li>
                <button 
                  className={`dropdown-item font-sans text-xs ${selectedPeriod === 30 ? 'active fw-bold' : ''}`}
                  onClick={() => { setSelectedPeriod(30); setIsPeriodDropdownOpen(false); }}
                  type="button"
                >
                  Próximos 30 dias (Mês Completo)
                </button>
              </li>
              <li>
                <button 
                  className={`dropdown-item font-sans text-xs ${selectedPeriod === 60 ? 'active fw-bold' : ''}`}
                  onClick={() => { setSelectedPeriod(60); setIsPeriodDropdownOpen(false); }}
                  type="button"
                >
                  Próximos 60 dias (Bimestre)
                </button>
              </li>
              <li>
                <hr className="dropdown-divider" />
              </li>
              <li>
                <button 
                  className={`dropdown-item font-sans text-xs ${selectedPeriod === 0 ? 'active fw-bold' : ''}`}
                  onClick={() => { setSelectedPeriod(0); setIsPeriodDropdownOpen(false); }}
                  type="button"
                >
                  Ver Todos (Sem Filtro de Data)
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bento Mini-Cards Grid (Page 4) */}
      <div className="row g-3 mb-4" id="bento-orders-counts">
        <div className="col-12 col-md-4">
          <div className="card shadow-sm border border-secondary border-opacity-10 bg-white p-3 rounded">
            <div className="d-flex align-items-center justify-content-between">
              <div className="p-2.5 bg-danger bg-opacity-10 text-danger rounded">
                <Wrench size={22} strokeWidth={2.5} />
              </div>
              <div className="text-end">
                <span className="text-muted text-xs font-sans uppercase font-bold tracking-wider d-block">pedidos</span>
                <span className="fw-black text-dark font-display d-block" style={{ fontSize: '1.8rem', lineHeight: 1.1 }}>
                  {summary.totalOrders}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-4">
          <div className="card shadow-sm border border-secondary border-opacity-10 bg-white p-3 rounded">
            <div className="d-flex align-items-center justify-content-between">
              <div className="p-2.5 bg-danger bg-opacity-10 text-danger rounded">
                <Wrench size={22} strokeWidth={2.5} />
              </div>
              <div className="text-end">
                <span className="text-muted text-xs font-sans uppercase font-bold tracking-wider d-block">produtos</span>
                <span className="fw-black text-dark font-display d-block" style={{ fontSize: '1.8rem', lineHeight: 1.1 }}>
                  {summary.totalProducts}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-4">
          <div className="card shadow-sm border border-secondary border-opacity-10 bg-white p-3 rounded">
            <div className="d-flex align-items-center justify-content-between">
              <div className="p-2.5 bg-danger bg-opacity-10 text-danger rounded">
                <Wrench size={22} strokeWidth={2.5} />
              </div>
              <div className="text-end">
                <span className="text-muted text-xs font-sans uppercase font-bold tracking-wider d-block">Em produção</span>
                <span className="fw-black text-dark font-display d-block" style={{ fontSize: '1.8rem', lineHeight: 1.1 }}>
                  {summary.inProduction}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Styled Search Bar (Page 4 style) */}
      <form
        ref={searchFormRef}
        onSubmit={(e) => e.preventDefault()}
        className="card shadow-sm border border-secondary border-opacity-10 bg-white p-2.5 mb-4 rounded-3"
        id="search-orders-form"
      >
        <div className="row g-2 align-items-center">
          {/* Text Search Field */}
          <div className="col-12 col-md-5 d-flex align-items-center">
            <Search size={18} className="text-muted ms-2 me-1" />
            <input
              type="text"
              name="search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="form-control border-0 py-2 text-dark font-sans shadow-none bg-transparent"
              placeholder="Buscar por Nº do pedido, Cliente, Cidade ou Status..."
              id="search-input-field"
              style={{ fontSize: '0.9rem' }}
            />
          </div>

          {/* Status Filter Selector */}
          <div className="col-12 col-sm-6 col-md-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-select border rounded text-dark bg-light-subtle shadow-none"
              style={{ fontSize: '0.85rem', height: '38px', borderColor: '#dee2e6' }}
            >
              <option value="">Status: Todos</option>
              <option value="Aguardando">Aguardando</option>
              <option value="Em Produção">Em Produção</option>
              <option value="Finalizado">Finalizado</option>
              <option value="Atrasado">Atrasado</option>
              {statusList
                .filter(st => !['aguardando', 'em produção', 'finalizado', 'atrasado'].includes(st.name.toLowerCase()))
                .map(st => (
                  <option key={st.id} value={st.name}>{st.name}</option>
                ))}
            </select>
          </div>

          {/* Buttons: Buscar / Limpar */}
          <div className="col-12 col-sm-6 col-md-4 d-flex gap-2 justify-content-sm-end">
            {(searchText || statusFilter) && (
              <button
                type="button"
                onClick={() => {
                  setSearchText("");
                  setStatusFilter("");
                }}
                className="btn btn-outline-danger btn-sm px-3 py-1.5 fw-bold font-sans rounded text-xs text-uppercase"
                style={{ height: '38px' }}
              >
                Limpar Filtros
              </button>
            )}
            <button
              type="submit"
              className="btn btn-dark btn-sm px-3.5 py-1.5 fw-bold d-flex align-items-center justify-content-center gap-1.5 text-xs text-uppercase font-sans rounded"
              style={{ height: '38px' }}
            >
              <Search size={15} /> Buscar
            </button>
          </div>
        </div>
      </form>

      {/* Primary Orders Sequential Table */}
      <div className="card shadow-sm border border-secondary border-opacity-10 bg-white rounded overflow-hidden mb-5">
        {loading ? (
          <div className="d-flex flex-column align-items-center justify-content-center p-5 text-muted">
            <div className="spinner-border text-primary speed-slow mb-3" role="status"></div>
            <p className="font-sans text-sm">Carregando fluxo operacional...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="p-5 text-center text-muted">
            <FileText size={44} className="mx-auto mb-2 text-muted opacity-50" />
            <p className="font-bold text-base mb-1">Nenhum pedido localizado</p>
            <p className="text-xs">Tente redefinir seu termo ou cadastrar um novo pedido.</p>
          </div>
        ) : (
          <>
            {/* Primary Orders Sequential Table - Desktop view */}
            <div className="table-responsive d-none d-lg-block">
            <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.85rem' }}>
              <thead className="table-light">
                <tr className="border-bottom border-light">
                  <th className="px-4 py-2.5 fw-bold text-secondary text-uppercase text-xs" style={{ width: '90px' }}>Pedido</th>
                  <th className="py-2.5 fw-bold text-secondary text-uppercase text-xs">Cliente</th>
                  <th className="py-2.5 fw-bold text-secondary text-uppercase text-xs">Cidade</th>
                  <th className="py-2.5 fw-bold text-secondary text-uppercase text-xs" style={{ width: '100px' }}>Data</th>
                  <th className="py-2.5 fw-bold text-secondary text-uppercase text-xs" style={{ width: '130px' }}>Dia</th>
                  <th className="py-2.5 fw-bold text-secondary text-uppercase text-xs text-end" style={{ width: '220px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => {
                  const isExpanded = expandedOrderId === order.id;
                  
                  // Condition: EMERGENCY Order gets a solid highlight red row status (like Row 6 in Page 4)
                  const isRedRow = order.isEmergence === true || order.isEmergence === 1 || String(order.isEmergence) === '1';

                  const rowStyle: React.CSSProperties = isRedRow ? {
                    backgroundColor: '#dc3545',
                    color: '#ffffff',
                    transition: 'all 0.15s ease'
                  } : {
                    transition: 'all 0.15s ease'
                  };

                  const textClass = isRedRow ? 'text-white' : 'text-dark';
                  const muteClass = isRedRow ? 'text-white-50' : 'text-muted';

                  // Estilo extra nas TDs para forçar a cor e evitar conflitos com o background branco nativo de tabelas do Bootstrap 5 (remover sombra do Bootstrap que tampa a cor real)
                  const cellBgStyle = isRedRow ? { backgroundColor: '#dc3545', color: '#ffffff', boxShadow: 'none' } : {};

                  return (
                    <React.Fragment key={order.id}>
                      {/* Parent Order Row */}
                      <tr
                        style={rowStyle}
                        className={`cursor-pointer ${isRedRow ? 'bg-danger text-white' : isExpanded ? 'table-active' : ''}`}
                        onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}
                        id={`order-row-${order.id}`}
                      >
                        <td className="px-4 py-3 fw-bold font-mono" style={cellBgStyle}>
                          {order.number}
                        </td>
                        <td className={`py-3 fw-semibold ${textClass}`} style={cellBgStyle}>
                          {order.customerName}
                        </td>
                        <td className={`py-3 ${textClass}`} style={cellBgStyle}>
                          {order.city}
                        </td>
                        <td className={`py-3 font-mono ${muteClass}`} style={cellBgStyle}>
                          {formatBrazilianDate(order.departure)}
                        </td>
                        <td className={`py-3 ${muteClass}`} style={cellBgStyle}>
                          {order.dayOfWeek}
                        </td>
                        <td className="py-3 text-end" style={cellBgStyle}>
                          <div className="d-flex align-items-center justify-content-end gap-3 px-2">
                            {/* Alert Triangle for Priority active on normal rows */}
                            {!isRedRow && order.isPriority && (
                              <AlertTriangle size={18} fill="#ffc107" className="text-dark" strokeWidth={1.5} />
                            )}
                            
                            {/* Status dropdown custom block matching screenshot Page 4 */}
                            {(() => {
                              const customStatusObj = statusList.find(st => st.name === order.customStatus);
                              const hasCustomStatus = !!order.customStatus;
                              const statusText = hasCustomStatus ? order.customStatus : order.statusName;
                              
                              const bgCol = isRedRow 
                                ? '#ffffff' 
                                : (hasCustomStatus && customStatusObj?.colorHex ? customStatusObj.colorHex : getSectorBgColorHex(order.statusName));
                              
                              const textCol = isRedRow 
                                ? '#ff0000' 
                                : '#ffffff';
                              
                              return (
                                <span 
                                  className={`badge font-sans font-bold text-xs py-2 px-3 text-uppercase border-0 d-inline-flex align-items-center gap-1.5`}
                                  style={{ 
                                    backgroundColor: bgCol, 
                                    color: textCol,
                                    borderRadius: '4px' 
                                  }}
                                >
                                  <span>{statusText}</span>
                                  {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                </span>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Subtable (Composition of Items - Page 4 Expanded Details) */}
                      {isExpanded && order.items && order.items.length > 0 && (
                        <tr id={`order-expand-${order.id}-sub`}>
                          <td colSpan={6} className="p-0 border-0">
                            <div className="bg-light p-3 border-start border-end border-bottom">
                              <div className="card shadow-sm border-0 rounded overflow-hidden">
                                <div className="card-header bg-dark text-white py-1 px-3 d-flex justify-content-between align-items-center">
                                  <span className="text-xxs tracking-wider uppercase font-mono fw-bold text-white-50">Ítens & Peças anexas</span>
                                  <span className="badge bg-secondary font-mono text-xxs">{order.items.length} itens</span>
                                </div>
                                <div className="table-responsive">
                                  <table className="table align-middle table-sm mb-0 table-borderless" style={{ fontSize: '0.8rem' }}>
                                    <thead className="table-light border-bottom">
                                      <tr>
                                        <th className="px-3 py-1.5 text-center text-secondary uppercase font-mono text-xxs" style={{ width: '80px' }}>Ítem</th>
                                        <th className="py-1.5 text-secondary uppercase font-mono text-xxs">Produto</th>
                                        <th className="px-3 py-1.5 text-end text-secondary uppercase font-mono text-xxs" style={{ width: '180px' }}>Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {order.items.map((item, idx) => {
                                        const itemNum = (idx + 1).toString().padStart(2, '0');
                                        const sectorHex = getSectorBgColorHex(item.setor || item.status, item.colorHex);

                                        return (
                                          <tr 
                                            key={idx} 
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleOpenProductionModal(
                                                order.number,
                                                itemNum,
                                                item.productName,
                                                order.customerName,
                                                order.city,
                                                order.departure,
                                                order.dayOfWeek
                                              );
                                            }}
                                            className="border-bottom border-light"
                                            style={{ 
                                              cursor: 'pointer' 
                                            }}
                                            title="Clique para gerenciar período de produção"
                                          >
                                            <td 
                                              className="px-3 py-2 text-center font-mono fw-bold border-0 text-white"
                                              style={{ backgroundColor: sectorHex }}
                                            >
                                              {itemNum}
                                            </td>
                                            <td 
                                              className="py-2 p-2 fw-semibold border-0 text-white text-start"
                                              style={{ backgroundColor: sectorHex }}
                                            >
                                              <div className="d-flex align-items-center justify-content-between text-start">
                                                <div className="d-flex flex-column text-start">
                                                  <span className="text-white">{item.productName}</span>
                                                  {item.periods && item.periods.length > 0 && calculateTotalTimeForPeriods(item.periods) && (
                                                    <span className="text-white-50 text-xxs font-mono" style={{ fontSize: '0.70rem', marginTop: '2px' }}>
                                                      Tempo total: {calculateTotalTimeForPeriods(item.periods)}
                                                    </span>
                                                  )}
                                                </div>
                                                <span className="badge text-white font-mono text-xxs" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', border: '1px solid rgba(255, 255, 255, 0.3)', padding: '2px 6px', fontSize: '0.65rem' }}>
                                                  Lançar Período
                                                </span>
                                              </div>
                                            </td>
                                            <td 
                                              className="px-3 py-2 text-end fw-black border-0 text-white"
                                              style={{ backgroundColor: sectorHex }}
                                            >
                                              <div className="d-inline-flex align-items-center gap-1 uppercase tracking-wide">
                                                <CheckCircle2 size={13} className="opacity-75" />
                                                <span>{item.status}</span>
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Visualização de Cartões Mobile para Celulares e Tablets em PWA (d-block d-lg-none) */}
          <div className="d-block d-lg-none p-2 bg-light-subtle" id="mobile-orders-cards-list">
            {filteredOrders.map((order) => {
              const isExpanded = expandedOrderId === order.id;
              const isRedRow = order.isEmergence === true || order.isEmergence === 1 || String(order.isEmergence) === '1';

              const borderStyle = isRedRow 
                ? { borderLeft: '5px solid #ff0000' } 
                : { borderLeft: '5px solid #4f5d75' };

              return (
                <div 
                  key={order.id} 
                  className="card mb-3 shadow-sm border border-secondary border-opacity-15 rounded-3 bg-white overflow-hidden"
                  style={borderStyle}
                >
                  {/* Cabeçalho do Card */}
                  <div 
                    className="card-body p-3 cursor-pointer"
                    onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}
                    style={{ minHeight: '48px' }}
                  >
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <span className="badge bg-dark text-warning font-mono text-xs px-2 py-1 rounded">
                          #{order.number}
                        </span>
                        {isRedRow && (
                          <span className="badge bg-danger text-white font-mono text-xxs uppercase ms-1 fw-bold">
                            Emergência
                          </span>
                        )}
                        {!isRedRow && order.isPriority && (
                          <span className="badge bg-warning text-dark font-mono text-xxs uppercase ms-1 fw-bold">
                            Prioridade
                          </span>
                        )}
                      </div>
                      {(() => {
                        const customStatusObj = statusList.find(st => st.name === order.customStatus);
                        const hasCustomStatus = !!order.customStatus;
                        const statusText = hasCustomStatus ? order.customStatus : order.statusName;
                        
                        const bgCol = isRedRow 
                          ? '#ffffff' 
                          : (hasCustomStatus && customStatusObj?.colorHex ? customStatusObj.colorHex : getSectorBgColorHex(order.statusName));
                        
                        const textCol = isRedRow 
                          ? '#ff0000' 
                          : '#ffffff';
                        
                        return (
                          <span 
                            className="badge font-sans text-xxs px-2.5 py-1.5 text-uppercase border-0 fw-bold d-inline-flex align-items-center gap-1"
                            style={{ 
                              backgroundColor: bgCol, 
                              color: textCol,
                              borderRadius: '4px'
                            }}
                          >
                            {statusText}
                          </span>
                        );
                      })()}
                    </div>

                    <h6 className="card-title text-dark fw-bold mb-1 text-sm">{order.customerName}</h6>
                    
                    <div className="d-flex flex-wrap gap-2 text-muted font-mono mt-2" style={{ fontSize: '0.72rem' }}>
                      <span className="d-inline-flex align-items-center gap-1 bg-light px-2 py-1 rounded">
                        <MapPin size={11} /> {order.city}
                      </span>
                      <span className="d-inline-flex align-items-center gap-1 bg-light px-2 py-1 rounded">
                        <Calendar size={11} /> {formatBrazilianDate(order.departure)} ({order.dayOfWeek})
                      </span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top border-light text-muted" style={{ fontSize: '0.75rem' }}>
                      <span className="fw-semibold text-dark">{order.items?.length || 0} peças</span>
                      <span className="text-primary fw-bold d-inline-flex align-items-center gap-0.5">
                        {isExpanded ? <>Recolher <ChevronUp size={14} /></> : <>Ver itens ({order.items?.length || 0}) <ChevronDown size={14} /></>}
                      </span>
                    </div>
                  </div>

                  {/* Seção Expandida Mobile contendo os Itens */}
                  {isExpanded && order.items && order.items.length > 0 && (
                    <div className="bg-light p-2 border-top">
                      <div className="text-xxs tracking-wider uppercase font-mono fw-bold text-secondary mb-2 px-1">Itens & Peças Anexas:</div>
                      <div className="d-flex flex-column gap-2">
                        {order.items.map((item, idx) => {
                          const itemNum = (idx + 1).toString().padStart(2, '0');
                          const sectorHex = getSectorBgColorHex(item.setor || item.status, item.colorHex);

                          return (
                            <div 
                              key={idx}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenProductionModal(
                                  order.number,
                                  itemNum,
                                  item.productName,
                                  order.customerName,
                                  order.city,
                                  order.departure,
                                  order.dayOfWeek
                                );
                              }}
                              className="p-3 rounded border border-light shadow-xs d-flex justify-content-between align-items-center cursor-pointer"
                              style={{ 
                                backgroundColor: sectorHex,
                                color: '#ffffff',
                                minHeight: '44px'
                              }}
                            >
                              <div className="text-start">
                                <div className="d-flex align-items-center gap-2">
                                  <span className="badge text-white font-mono font-bold" style={{ backgroundColor: 'rgba(255, 255, 255, 0.35)', border: '1px solid rgba(255, 255, 255, 0.4)', fontSize: '0.75rem' }}>
                                    {itemNum}
                                  </span>
                                  <span className="text-white fw-semibold text-xs d-block">{item.productName}</span>
                                </div>
                                {item.periods && item.periods.length > 0 && calculateTotalTimeForPeriods(item.periods) && (
                                  <span className="text-white bg-dark bg-opacity-25 px-1.5 py-0.5 rounded text-xxs font-mono d-inline-block mt-1 ps-1" style={{ fontSize: '0.65rem' }}>
                                    Tempo: {calculateTotalTimeForPeriods(item.periods)}
                                  </span>
                                )}
                              </div>
                              <div className="d-flex flex-column align-items-end gap-1">
                                <span className="badge text-white font-mono text-xxxs px-1.5 py-0.5 fw-bold" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', border: '1px solid rgba(255, 255, 255, 0.3)' }}>
                                  GRAVAR PERÍODO
                                </span>
                                <span className="text-xxs fw-bold uppercase tracking-wide opacity-90 d-inline-flex align-items-center gap-0.5">
                                  <CheckCircle2 size={12} /> {item.status}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>

      {/* Production Modal monitoring */}
      <ProductionModal
        isOpen={productionModalOpen}
        onClose={() => setProductionModalOpen(false)}
        orderNumber={selectedOrderNumber}
        itemId={selectedItemId}
        itemDescription={selectedItemDescription}
        clientName={selectedClientName}
        city={selectedCity}
        deliveryDate={selectedDeliveryDate}
        weekday={selectedWeekday}
        activeSector={activeOperator?.role || "Dobra"}
        orderItems={(() => {
          const sOrd = orders.find(o => String(o.number) === selectedOrderNumber);
          return sOrd ? sOrd.items : [];
        })()}
        initialPeriods={(() => {
          const sOrd = orders.find(o => String(o.number) === selectedOrderNumber);
          const idx = sOrd ? (parseInt(selectedItemId) - 1) : -1;
          return sOrd && idx >= 0 && idx < sOrd.items.length ? (sOrd.items[idx].periods || []) : [];
        })()}
        onStart={(formData) => {
          // Local handleAddRow handles adding dynamically.
        }}
        onSave={(formData) => {
          const oNum = formData.get('order_number') as string;
          const iId = formData.get('item_id') as string;
          const sect = formData.get('active_sector') as string;
          const periodsJson = formData.get('periods_json') as string;

          let parsedPeriods: any[] = [];
          try {
            if (periodsJson) {
              parsedPeriods = JSON.parse(periodsJson);
            }
          } catch (e) {
            console.error("Failed to parse periods JSON", e);
          }

          // Obter os períodos iniciais para este setor para checar alterações reais
          const sOrd = orders.find(o => String(o.number) === oNum);
          const idx = sOrd ? (parseInt(iId) - 1) : -1;
          const initialItemPeriods = sOrd && idx >= 0 && idx < sOrd.items.length ? (sOrd.items[idx].periods || []) : [];
          const initialSectorPeriods = initialItemPeriods.filter(p => (p.sector || "").toLowerCase() === sect.toLowerCase());

          // Função de comparação para evitar falso-positivo (ex: strings vazias vs undefined/null)
          const isSamePeriod = (p1: any, p2: any) => {
            return (p1.start || "").trim() === (p2.start || "").trim() &&
                   (p1.finish || "").trim() === (p2.finish || "").trim() &&
                   (p1.date || "") === (p2.date || "") &&
                   (p1.collaborator || "").trim() === (p2.collaborator || "").trim() &&
                   (p1.occurrences || "").trim() === (p2.occurrences || "").trim();
          };

          const periodsChanged = initialSectorPeriods.length !== parsedPeriods.length ||
            parsedPeriods.some((p2, i) => {
              const p1 = initialSectorPeriods[i];
              return !p1 || !isSamePeriod(p1, p2);
            });

          if (!periodsChanged) {
            setProductionModalOpen(false);
            alert("Nenhuma alteração foi realizada. Os dados e o status do pedido permaneceram inalterados.");
            return;
          }

          // Combinar períodos de outros setores com os novos períodos deste setor ativo
          const allItemPeriods = [
            ...(initialItemPeriods).filter(p => (p.sector || "").toLowerCase() !== sect.toLowerCase()),
            ...parsedPeriods
          ];

          // Determinar se existe algum período de produção em andamento (iniciado mas não pausado/finalizado) em qualquer setor
          const activePeriod = allItemPeriods.find(p => p.start && p.start.trim() !== "" && (!p.finish || p.finish.trim() === ""));
          
          let targetStatus = "Aguardando";
          let targetSector = sect;

          if (activePeriod) {
            targetStatus = `Produção/${activePeriod.sector}`;
            targetSector = activePeriod.sector;
          } else {
            // Se não houver período ativo, o status do item é "Aguardando"
            // Mas o setor do item deve ser o setor atual dele para não reverter indevidamente
            const currentItem = sOrd && idx >= 0 && idx < sOrd.items.length ? sOrd.items[idx] : null;
            targetSector = currentItem ? currentItem.setor : sect;
          }

          const updateStatus = (list: OrderListItem[]) => list.map(order => {
            if (String(order.number) === oNum) {
              const updatedItems = order.items.map((item, idx) => {
                const itemNumStr = (idx + 1).toString().padStart(2, "0");
                if (itemNumStr === iId) {
                  return { 
                    ...item, 
                    status: targetStatus, 
                    setor: targetSector,
                    colorHex: getSectorBgColorHex(targetSector),
                    periods: allItemPeriods
                  };
                }
                return item;
              });

              // Check if any item in the order is actively in production
              const hasInProduction = updatedItems.some(it => 
                it.status.toLowerCase().includes('produção') || 
                it.status.toLowerCase().includes('producao')
              );
              
              // Se todos os itens estão finalizados, o status geral é Finalizado.
              // Se tem algum em produção, o status geral é Produção.
              // Caso contrário, é Aguardando.
              const allFinished = updatedItems.length > 0 && updatedItems.every(it => 
                it.status.toLowerCase() === 'finalizado' || it.isProduced === true
              );

              const statusName = allFinished ? "Finalizado" : (hasInProduction ? "Produção" : "Aguardando");

              return {
                ...order,
                items: updatedItems,
                statusName
              };
            }
            return order;
          });

          const updated = updateStatus(orders);
          setOrders(updated);
          setFilteredOrders(updated);
          OrdersModel.saveOrders(updated);
          
          // Sincronizar com o servidor MySQL
          const orderToUpdate = updated.find(o => String(o.number) === oNum);
          if (orderToUpdate) {
            OrdersModel.updateOrderOnServer(orderToUpdate).catch(err => {
              console.error("Erro ao sincronizar com o banco:", err);
            });
          }

          setProductionModalOpen(false);
          if (targetStatus.startsWith("Produção")) {
            alert(`Dados gravados com sucesso!\nO status do item foi alterado para Produção/${targetSector} e o status geral do pedido #${oNum} foi atualizado para Produção.`);
          } else {
            alert(`Dados gravados com sucesso!\nNenhum período de produção ativo foi detectado em andamento. O status do item permanece como 'Aguardando' e o status geral do pedido #${oNum} foi atualizado.`);
          }
        }}
        onFinish={(formData) => {
          const oNum = formData.get('order_number') as string;
          const iId = formData.get('item_id') as string;
          const sect = formData.get('active_sector') as string;
          const periodsJson = formData.get('periods_json') as string;

          let parsedPeriods = [];
          try {
            if (periodsJson) {
              parsedPeriods = JSON.parse(periodsJson);
            }
          } catch (e) {
            console.error("Failed to parse periods JSON", e);
          }

          const nextSec = getNextSector(sect);

          const updateStatus = (list: OrderListItem[]) => list.map(order => {
            if (String(order.number) === oNum) {
              const updatedItems = order.items.map((item, idx) => {
                const itemNumStr = (idx + 1).toString().padStart(2, "0");
                if (itemNumStr === iId) {
                  if (nextSec) {
                    return { 
                      ...item, 
                      status: "Aguardando", 
                      setor: nextSec.name,
                      colorHex: nextSec.colorHex,
                      periods: [
                        ...(item.periods || []).filter(p => (p.sector || "").toLowerCase() !== sect.toLowerCase()),
                        ...parsedPeriods
                      ]
                    };
                  } else {
                    return {
                      ...item,
                      status: "Finalizado",
                      setor: "Finalizado",
                      colorHex: "#6c757d",
                      isProduced: true,
                      periods: [
                        ...(item.periods || []).filter(p => (p.sector || "").toLowerCase() !== sect.toLowerCase()),
                        ...parsedPeriods
                      ]
                    };
                  }
                }
                return item;
              });

              // Recalculate order status:
              // 1. If ALL items in the order are finished (either status is 'Finalizado' or isProduced is true), the order status is "Finalizado".
              // 2. Otherwise, if any item is in active production (status has 'Produção' / 'Producao'), the order status is "Produção".
              // 3. Otherwise, the order status returns to "Aguardando".
              const allItemsFinished = updatedItems.length > 0 && updatedItems.every(it => 
                it.status.toLowerCase() === 'finalizado' || it.isProduced === true
              );

              const hasInProduction = updatedItems.some(it => 
                it.status.toLowerCase().includes('produção') || 
                it.status.toLowerCase().includes('producao')
              );
              
              let statusName = order.statusName;
              if (allItemsFinished) {
                statusName = "Finalizado";
              } else if (hasInProduction) {
                statusName = "Produção";
              } else {
                statusName = "Aguardando";
              }

              return {
                ...order,
                items: updatedItems,
                statusName
              };
            }
            return order;
          });

          const updated = updateStatus(orders);
          setOrders(updated);
          setFilteredOrders(updated);
          OrdersModel.saveOrders(updated);

          // Sincronizar com o servidor MySQL
          const orderToUpdate = updated.find(o => String(o.number) === oNum);
          if (orderToUpdate) {
            OrdersModel.updateOrderOnServer(orderToUpdate).catch(err => {
              console.error("Erro ao sincronizar com o banco:", err);
            });
          }

          setProductionModalOpen(false);
          const msg = nextSec 
            ? `Processo do setor ${sect} finalizado! O item foi encaminhado ao próximo setor (${nextSec.name}) e seu status retornou para Aguardando.`
            : `Processo de instalação finalizado com sucesso! O produto foi totalmente concluído.`;
          alert(msg);
        }}
      />
    </div>
  );
}
